import './ActionRequiredGrid.scss';
import {
    Column,
    Format,
    Lookup,
} from 'devextreme-react/data-grid';

import { useGetActionRequiredOrders } from "../api/get-action-required-orders";
import { ActionRequiredGridProps } from '../types/ActionRequiredGridProps';
import { Link, useLocation } from 'react-router-dom';
import { LoadPagePanel, SyncDataGrid } from '../../../../components';
import { AR, InstallerType, OrderTypes } from '../../../../types/enums';
import { formatShortTime, getCurrentDate } from '../../../../utils/date-utils';
import { useCallback } from 'react';
import { useGetCompany } from '../../../../api/company';
import { useAuth } from '../../../../contexts/auth';

export default function ActionRequiredGrid(props: ActionRequiredGridProps) {
    const orders = useGetActionRequiredOrders(props.actionRequiredID, props.pendingOnly, props.pastDueOnly, props.activeOnly);

    const location = useLocation();
    const returnUrl = encodeURIComponent(location.pathname + location.search);

	const { user } = useAuth();
	const company = useGetCompany(user?.companyId!);

    const isCountertopInstaller = company.data?.installerTypes.includes(InstallerType.CFI);
    const isCabinetInstaller = company.data?.installerTypes.includes(InstallerType.CCI);
    const isPlumbingInstaller = company.data?.installerTypes.includes(InstallerType.PlumbingInstaller);

    function getActionRequiredUrl(order: any) {
        var actionRequiredID = order.actionRequiredID;
        if (actionRequiredID === 111 || actionRequiredID === 112) {
            actionRequiredID = 110;
        }

        if (actionRequiredID === AR.CfiValidateChangeOrder) {
            actionRequiredID = 120;
        }

        if (actionRequiredID === AR.CfiUploadPostTemplateDrawing) {
            actionRequiredID = order.orderTypeID === OrderTypes.MeasureOrder ? 115 : 120;
        }
        
        if (actionRequiredID === 155 || actionRequiredID === 156 || actionRequiredID === 157) {
            actionRequiredID = 145;
        }
        
        let url;

        if (order.orderTypeID === OrderTypes.MaterialOrder) {
            url = order.retailOrderID ?
                `/cfi/retail-order/${order.retailOrderID}/130/${order.orderID}?returnUrl=${returnUrl}` :
                `/cfi/material-order/${order.orderID}?returnUrl=${returnUrl}`;
        } else if (order.orderTypeID === OrderTypes.WorkOrder) {
            url = `/cfi/work-order/${order.orderID}/${order.actionRequiredID}?returnUrl=${returnUrl}`;
        } else if (order.orderTypeID === OrderTypes.Estimate || order.orderTypeID === OrderTypes.Lead) {
            url = `/cfi/estimate/${order.orderID}/${order.actionRequiredID}?returnUrl=${returnUrl}`;
        } else {
            url = `/cfi/retail-order/${order.orderID}/${actionRequiredID}?returnUrl=${returnUrl}`;
        }

        return url;
    }

    const currentDate = getCurrentDate();

    const rowPrepared = useCallback((e: any) => {
        if (e.rowType === "data") {
            if (e.data.pendingEndDate && e.data.pendingEndDate < currentDate) {
                e.rowElement.classList.add("highlighted-pending");
            }
            if (e.data.isQuickShip) {
                e.rowElement.classList.add("quick-ship-order-highlighted");
            }
        }
    }, []);

    return (
        <>
            <SyncDataGrid
                stateStorageKey={'action-required-orders'}
                className={'dx-card wide-card action-required-grid'}
                dataSource={orders.data?.orders}
                keyExpr={['actionRequiredID', 'orderID']}
                height="68vh"
                onRowPrepared={rowPrepared}
            >
                <Column
                    dataField={'orderNumber'}
                    caption={'Order #'}
                    dataType="string"
                    cellRender={(cell: any) => {
                        return <Link to={getActionRequiredUrl(cell.data)}>
                            {cell.data.orderNumber}
                        </Link>;
                    }}
                />
                <Column
                    dataField={'projectNumber'}
                    caption={'Project #'}
                    dataType="string"
                />
                <Column
                    dataField={'date'}
                    caption={'Customer Order Date'}
                    dataType="date"
                />
                <Column
                    dataField={'actionRequiredID'}
                    caption={'Action Required'}
                    dataType="string"
                    cellRender={(cell: any) => {
                        return <Link to={getActionRequiredUrl(cell.data)}>
                            {cell.data.actionDescription}
                        </Link>;
                    }}
                    editorOptions={{
                        dropDownOptions: {
                            minWidth: 250
                        }
                    }}
                >
                    <Lookup
                        dataSource={orders.data?.actionsRequired}
                        valueExpr={'actionRequiredID'}
                        displayExpr={'description'}
                    />
                </Column>
                <Column
                    dataField={'actionRequiredStatusID'}
                    calculateDisplayValue={'actionRequiredStatus'}
                    caption={'AR Status'}
                    dataType="string"
                    editorOptions={{
                        dropDownOptions: {
                            minWidth: 150,
                        }
                    }}>
                    <Lookup
                        dataSource={orders.data?.arStatuses}
                        valueExpr={'lookupID'}
                        displayExpr={'displayValue'}
                    />
                </Column>
                <Column
                    dataField={'amount'}
                    caption={'Order Amount'}
                    dataType="number"
                    editorOptions={{ format: '$#,##0.##', showClearButton: true }}
                >
                    <Format type="currency" precision={2}></Format>
                </Column>
                <Column
                    dataField={'orderTypeID'}
                    calculateDisplayValue={'orderType'}
                    caption={'Order Type'}
                    dataType="string"
                    editorOptions={{
                        dropDownOptions: {
                            minWidth: 150,
                        }
                    }}
                >
                    <Lookup
                        dataSource={orders.data?.orderTypes}
                        valueExpr={'lookupID'}
                        displayExpr={'displayValue'}
                    />
                </Column>
                <Column
                    dataField={'cabinetDate'}
                    caption={'Cabinet Install Date'}
                    dataType="date"
                />
                <Column
                    dataField={'dealerNumber'}
                    caption={'Store #'}
                    dataType="string"
                />
                <Column
                    dataField={'proxyStoreNumber'}
                    caption={'Proxy Store #'}
                    dataType="string"
                />
                <Column
                    dataField={'productLine'}
                    caption={'Product Line'}
                    dataType="string"
                    editorOptions={{
                        dropDownOptions: {
                            minWidth: 150
                        }
                    }}
                >
                    <Lookup
                        dataSource={orders.data?.productLines}
                        valueExpr={'description'}
                        displayExpr={'description'}
                    />
                </Column>
                <Column
                    dataField={'customerName'}
                    caption={'Customer Name'}
                    dataType="string"
                />
                <Column
                    dataField={'customerAddress'}
                    caption={'Address'}
                    dataType="string"
                />
                <Column
                    dataField={'customerPhone'}
                    caption={'Customer Phone #'}
                    dataType="string"
                />
                <Column
                    dataField={'customerCityZip'}
                    caption={'City/State,Zip'}
                    dataType="string"
                />
                <Column
                    dataField={'projectManager'}
                    caption={'ProjectManager'}
                    dataType="string"
                />
                <Column
                    dataField={'readyToMeasureOrTemplateDate'}
                    caption={'Ready to Measure/Template Date'}
                    dataType="date"
                    visible={isCountertopInstaller}
                />
                <Column
                    dataField={'measureDate'}
                    caption={'Measure Date'}
                    dataType="date"
                    visible={isCountertopInstaller}
                />
                <Column
                    dataField={'enterTemplateDate'}
                    caption={'Template Date'}
                    dataType="date"
                    visible={isCountertopInstaller}
                />
                <Column
                    dataField={'scheduledInstallDate'}
                    caption={'Scheduled Install Date'}
                    dataType="date"
                    visible={isCountertopInstaller}
                />
                <Column
                    dataField={'actualInstallDate'}
                    caption={'Actual Install Date'}
                    dataType="date"
                    visible={isCountertopInstaller}
                />
                <Column
                    dataField={'actualInstallStartDate'}
                    caption={'Actual Install Start Date'}
                    dataType="date"
                    visible={isCabinetInstaller}
                />
                <Column
                    dataField={'lowerCabinetsPlumbLevelDate'}
                    caption={'Lower Cabinets Plumb & Level Date'}
                    dataType="date"
                    visible={isCabinetInstaller}
                />
                <Column
                    dataField={'actualInstallCompletionDate'}
                    caption={'Actual Install Completion Date'}
                    dataType="date"
                    visible={isCabinetInstaller}
                />
                <Column
                    dataField={'scheduledDisconnectDate'}
                    caption={'Scheduled Disconnect Date'}
                    dataType="date"
                    visible={isPlumbingInstaller}
                />
                <Column
                    dataField={'scheduledReconnectDate'}
                    caption={'Scheduled Reconnect Date'}
                    dataType="date"
                    visible={isPlumbingInstaller}
                />
                <Column
                    dataField={'userName'}
                    caption={'User'}
                    dataType="string"
                />
                <Column
                    dataField={'lastUpdatedDate'}
                    caption={'Date'}
                    dataType="date"
                />
                <Column
                    caption={'Time'}
                    dataType="string"
                    calculateDisplayValue={(row) => {
                        return formatShortTime(row.lastUpdatedDate)
                    }}
                />
                <Column
                    dataField={'pendingStartDate'}
                    caption={'Pending Start Date'}
                    dataType="date"
                />
                <Column
                    dataField={'pendingEndDate'}
                    caption={'Pending End Date'}
                    dataType="date"
                />
            </SyncDataGrid>

            <LoadPagePanel visible={orders.isLoading} />
        </>
    )
} 
